import type { ReactNode } from "react";
import { Link } from "react-router";
import { ArrowRight, ChevronRight, Cog, Package, Search } from "lucide-react";
import type { HomeShelves, ListingCard, MachineFamily, ProductType } from "../../../api/catalogue";
import { Icon } from "../../ui";
import { LoadError } from "../Notices";
import { PriceTag, StockBadge } from "../partLabels";

/** Just the fields MobileHome needs from a TanStack query, so the route stays the container. */
interface Loadable<T> {
  data: T | undefined;
  isPending: boolean;
  isError: boolean;
  refetch: () => void;
}

export interface MobileHomeProps {
  /** Home shelves: the popular-parts rows come from `featured`. */
  shelves: Loadable<HomeShelves>;
  /** Product types for the Departments grid. */
  departments: Loadable<ProductType[]>;
  /** Equipment categories for the "Find your machine" carousel. */
  machines: Loadable<MachineFamily[]>;
  /** The relocated Quick-order bulk form, rendered by the route (it owns the controller). */
  quickOrder: ReactNode;
  /** The relocated find-by-diagram machine picker, rendered by the route (it owns its state). */
  machinePicker: ReactNode;
}

/**
 * The phone (≤768px) Shop home, rebuilt to the mobile kit's information architecture
 * (`docs/design-system/screens/mobile/MobileScreens.jsx`): a tap-to-search pill, a benefit
 * hero, a machine carousel, a departments grid and popular-parts rows. Desktop keeps its own
 * layout (KALI-48). The kit's offer/free-delivery copy is dropped — promotions aren't in v1
 * (design system README §6) — and the parts-desk phone number waits on KALI-1, so "can't find
 * it?" points at the inquiry form. The sticky header and bottom tab bar come from StoreShell.
 */
export function MobileHome({ shelves, departments, machines, quickOrder, machinePicker }: MobileHomeProps) {
  const families = machines.data ?? [];
  const modelCount = families.reduce((sum, family) => sum + family.equipment_models.length, 0);
  const featured = shelves.data?.featured ?? [];

  return (
    <div className="flex flex-col gap-8 px-4 pt-4 pb-10">
      <Link
        to="/search"
        className="flex h-(--control-lg) items-center gap-3 rounded-full border border-border-default bg-surface-card px-5 type-body [font-size:var(--text-base)] text-text-muted shadow-xs"
      >
        <Icon icon={Search} size={20} className="flex-none" />
        <span className="truncate">Search part number or name</span>
      </Link>

      <section className="relative isolate overflow-hidden rounded-xl bg-green-900 p-6">
        <div aria-hidden="true" className="absolute inset-0 -z-10 bg-(image:--gradient-sprout) opacity-30" />
        <p className="m-0 type-eyebrow tracking-[var(--tracking-widest)] text-green-100 uppercase">Online parts catalogue</p>
        <h2 className="mt-3 mb-2 type-display [font-size:var(--text-2xl)] tracking-tight text-white">Find the right part by its diagram</h2>
        <p className="m-0 type-small text-green-100">Pick your machine and read the part number straight off the assembly drawing — order the right one first time.</p>
        <Link
          to="/equipment-categories"
          className="mt-5 inline-flex h-(--control-md) items-center gap-2 rounded-sm bg-surface-card px-5 type-label [font-size:var(--text-base)] font-bold text-green-800 hover:bg-green-50"
        >
          Browse machines <Icon icon={ArrowRight} size={18} />
        </Link>
      </section>

      {/* Find your machine — the single, prominent way in, wired to the Browse machines drill-down. */}
      <Carousel families={families} modelCount={modelCount} machines={machines} />

      {/* The power-user tools the kit home has no slot for, kept a tap away rather than dropped.
          The Designer placed them directly below the carousel, above Departments (KALI-48). */}
      <section>
        <Heading title="More ways to order" />
        <div className="flex flex-col gap-3">
          <Disclosure summary="Order by part number" detail="Add up to five numbers to your basket at once.">
            {quickOrder}
          </Disclosure>
          <Disclosure summary="Find a part on its assembly diagram" detail="Choose a machine type, model and assembly, then open the drawing.">
            {machinePicker}
          </Disclosure>
        </div>
      </section>

      {/* Departments */}
      <Departments departments={departments} />

      {/* Popular parts. Which parts belong here is KALI-32; until then these are the newest in
          stock, so the heading says that rather than the kit's "Ordered most this month". */}
      {shelves.isError ? (
        <section>
          <Heading title="New on the shelf" />
          <LoadError onRetry={() => shelves.refetch()} />
        </section>
      ) : shelves.isPending ? (
        <section>
          <Heading title="New on the shelf" />
          <div aria-busy="true" className="flex flex-col gap-3">
            {[0, 1, 2, 3].map((i) => (
              <div key={i} className="h-[68px] rounded-md border border-border-subtle bg-surface-card" />
            ))}
          </div>
        </section>
      ) : (
        featured.length > 0 && (
          <section>
            <Heading eyebrow="Just added" title="New on the shelf" />
            <div className="flex flex-col gap-3">
              {featured.map((listing) => (
                <PartRow key={listing.id} listing={listing} />
              ))}
            </div>
          </section>
        )
      )}

      <p className="m-0 px-2 text-center type-small text-text-muted">
        Can't find it? <Link to="/ask">Send us the details</Link> and we'll identify it for you.
      </p>
    </div>
  );
}

/** A section title, with an optional eyebrow above and a link at the right. */
function Heading({ eyebrow, title, action }: { eyebrow?: string; title: string; action?: ReactNode }) {
  return (
    <div className="mb-4 flex items-end justify-between gap-4">
      <div className="min-w-0">
        {eyebrow && <p className="mb-1 type-eyebrow tracking-[var(--tracking-widest)] text-green-700 uppercase">{eyebrow}</p>}
        <h2 className="m-0 type-h3 [font-size:var(--text-lg)]">{title}</h2>
      </div>
      {action && <div className="flex-none">{action}</div>}
    </div>
  );
}

/** The horizontal "Find your machine" carousel of equipment-category tiles. */
function Carousel({ families, modelCount, machines }: { families: MachineFamily[]; modelCount: number; machines: MobileHomeProps["machines"] }) {
  if (machines.isError) {
    return (
      <section>
        <Heading title="Find your machine" />
        <LoadError onRetry={() => machines.refetch()} />
      </section>
    );
  }
  if (machines.isPending) {
    return (
      <section>
        <Heading title="Find your machine" />
        <div className="-mx-4 flex gap-4 overflow-x-hidden px-4">
          {[0, 1, 2].map((i) => (
            <div key={i} className="h-[104px] w-[9.5rem] flex-none rounded-lg border border-border-subtle bg-surface-card" />
          ))}
        </div>
      </section>
    );
  }
  if (families.length === 0) return null;
  return (
    <section>
      <Heading
        title="Find your machine"
        action={
          <Link to="/equipment-categories" className="inline-flex min-h-11 items-center gap-1 border-b-0 type-label [font-size:var(--text-sm)] whitespace-nowrap">
            All {modelCount} {modelCount === 1 ? "machine" : "machines"} <Icon icon={ChevronRight} size={16} />
          </Link>
        }
      />
      <ul className="m-0 -mx-4 flex list-none snap-x snap-mandatory scroll-px-4 gap-4 overflow-x-auto px-4 pb-2">
        {families.map((family) => (
          <li key={family.id} className="flex-none snap-start">
            <Link
              to={`/equipment-categories#family-${family.id}`}
              className="flex h-full w-[9.5rem] flex-col rounded-lg border border-border-subtle bg-surface-card p-4 text-inherit shadow-sm hover:border-green-300 hover:shadow-md"
            >
              <Icon icon={Cog} size={22} className="mb-3 text-steel-500" />
              <span className="block type-h4 [font-size:var(--text-sm)] text-text-strong">{family.name}</span>
              <span className="mt-0.5 block type-small [font-size:var(--text-xs)] text-text-muted">
                {family.equipment_models.length} {family.equipment_models.length === 1 ? "model" : "models"}
              </span>
            </Link>
          </li>
        ))}
      </ul>
    </section>
  );
}

/** The 2-column Departments grid of product-type cards. */
function Departments({ departments }: { departments: MobileHomeProps["departments"] }) {
  const types = departments.data ?? [];
  if (departments.isError) {
    return (
      <section>
        <Heading title="Shop by department" />
        <LoadError onRetry={() => departments.refetch()} />
      </section>
    );
  }
  if (departments.isPending) {
    return (
      <section>
        <Heading title="Shop by department" />
        <div className="grid grid-cols-2 gap-3">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="h-[60px] rounded-md border border-border-subtle bg-surface-card" />
          ))}
        </div>
      </section>
    );
  }
  if (types.length === 0) return null;
  return (
    <section>
      <Heading
        title="Shop by department"
        action={
          <Link to="/product-types" className="inline-flex min-h-11 items-center gap-1 border-b-0 type-label [font-size:var(--text-sm)] whitespace-nowrap">
            All supplies <Icon icon={ChevronRight} size={16} />
          </Link>
        }
      />
      <ul className="m-0 grid list-none grid-cols-2 gap-3 p-0">
        {types.map((type) => (
          <li key={type.id}>
            <Link
              to={`/product-types/${type.id}`}
              className="flex min-h-[60px] items-center gap-3 rounded-md border border-border-subtle bg-surface-card p-4 text-inherit hover:border-green-300"
            >
              <Icon icon={Package} size={20} className="flex-none text-steel-500" />
              <span className="min-w-0">
                <span className="block type-h4 [font-size:var(--text-sm)] text-text-strong">{type.name}</span>
                <span className="block type-small [font-size:var(--text-xs)] text-text-muted">
                  {type.listing_count} {type.listing_count === 1 ? "item" : "items"}
                </span>
              </span>
            </Link>
          </li>
        ))}
      </ul>
    </section>
  );
}

/** One part as a tappable row — the phone stand-in for a table row (mobile kit PartRow). */
function PartRow({ listing }: { listing: ListingCard }) {
  return (
    <Link
      to={`/listings/${listing.id}`}
      className="flex min-h-[68px] items-center gap-4 rounded-md border border-border-subtle bg-surface-card p-3 text-inherit hover:border-green-300"
    >
      {/* A plain thumb at 44px: the full "not photographed yet" placeholder clips this small. */}
      <span className="flex size-11 flex-none items-center justify-center overflow-hidden rounded-sm border border-border-subtle bg-white text-steel-400">
        {listing.photo ? <img src={listing.photo.url} alt="" loading="lazy" className="size-full object-contain" /> : <Icon icon={Package} size={20} />}
      </span>
      <span className="min-w-0 flex-1">
        <span className="flex items-center gap-2">
          <span className="type-sku [font-size:var(--text-xs)] [overflow-wrap:anywhere] text-text-muted">{listing.reference_number ?? listing.sku}</span>
          {listing.origin === "genuine" && <span aria-hidden="true" className="size-1.5 flex-none rounded-full bg-gold-500" />}
        </span>
        <span className="mt-0.5 block type-body [font-size:var(--text-sm)] leading-snug text-text-strong">{listing.name}</span>
        <span className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1">
          <PriceTag cents={listing.price_cents} unit={listing.unit} size="sm" />
          <StockBadge inStock={listing.in_stock} />
        </span>
      </span>
      <Icon icon={ChevronRight} size={18} className="flex-none text-ink-400" />
    </Link>
  );
}

/** A collapsed, clearly-secondary entry that reveals a power-user tool on tap. */
function Disclosure({ summary, detail, children }: { summary: string; detail: string; children: ReactNode }) {
  return (
    <details className="group rounded-md border border-border-subtle bg-surface-card [&_summary::-webkit-details-marker]:hidden">
      <summary className="flex min-h-(--control-lg) cursor-pointer list-none items-center gap-3 p-4">
        <span className="min-w-0 flex-1">
          <span className="block type-h4 [font-size:var(--text-sm)] text-text-strong">{summary}</span>
          <span className="block type-small [font-size:var(--text-xs)] text-text-muted">{detail}</span>
        </span>
        <Icon icon={ChevronRight} size={18} className="flex-none text-ink-400 transition-transform group-open:rotate-90" />
      </summary>
      <div className="border-t border-border-subtle p-4">{children}</div>
    </details>
  );
}
